import { App, TierartEnum } from '@ps20-frontend/frontend-models';
import type { TgvOfferKind } from '../../app/angebot/angebot.models';
import type { TgvPricingEntry, TransientState } from '../reducers/transient-state.reducer';
import { hasPricedSelectedLinie } from './childPricing';
import { listedChildKeys } from './childKeys';
import { TgvTier, TgvVorvertrag, TiergruppeState } from './tiergruppe.model';

export interface AngebotInput {
  tiergruppe: TiergruppeState;
  transientState: Pick<TransientState, 'children' | 'pricing' | 'angebot'>;
  children: Record<string, unknown>;
  juristisch?: boolean;
}

export interface AngebotOffer {
  key: string;
  kind: TgvOfferKind;
  animalType: TierartEnum;
  names: string[];
  cardId: string | null;
  vorvertrag: TgvVorvertrag | null;
  state: unknown;
}

export const notDeferredChildKeys = (tiergruppe: TiergruppeState): string[] =>
  listedChildKeys(tiergruppe).filter(key => !(tiergruppe.deferredDeletion ?? []).includes(key));

export const selectableChildKeys = (
  tiergruppe: TiergruppeState,
  transientState: Pick<TransientState, 'children'>,
): string[] => {
  const { pendingDeletion, creating } = transientState.children;
  return notDeferredChildKeys(tiergruppe).filter(
    key => !pendingDeletion.includes(key) && !creating.includes(key),
  );
};

export const firstSelectableKey = (
  tiergruppe: TiergruppeState,
  transientState: Pick<TransientState, 'children'>,
): string | null => selectableChildKeys(tiergruppe, transientState)[0] ?? null;

export const tierName = (tiergruppe: TiergruppeState, tier: TgvTier): string =>
  tier.name.trim() === '' ? `Tier ${tiergruppe.tiere.indexOf(tier) + 1}` : tier.name.trim();

const toTkvOffer = (input: AngebotInput, key: string): AngebotOffer | null => {
  const { tiergruppe, children } = input;
  const tier = tiergruppe.tiere.find(candidate => tiergruppe.angebotsIds[candidate.cardId] === key);
  const state = children[key] ?? null;
  if (tier?.tierart == null || state === null) {
    return null;
  }
  return {
    key,
    kind: 'TKV',
    animalType: tier.tierart,
    names: [tierName(tiergruppe, tier)],
    cardId: tier.cardId,
    vorvertrag: tier.vorvertrag ?? null,
    state,
  };
};

const toThhOffer = (input: AngebotInput, key: string): AngebotOffer => {
  const { tiergruppe, children } = input;
  const isHund = key === App.THH_HUND;
  const animalType = isHund ? TierartEnum.HUND : TierartEnum.PFERD;
  const vorvertrag = tiergruppe.thhVorvertrag?.[isHund ? 'thhHund' : 'thhPferd'] ?? null;
  return {
    key,
    kind: isHund ? 'THH_HUND' : 'THH_PFERD',
    animalType,
    names: tiergruppe.tiere
      .filter(tier => tier.tierart === animalType && tier.umfang.thh)
      .map(tier => tierName(tiergruppe, tier)),
    cardId: null,
    vorvertrag,
    state: children[key],
  };
};

export const toAngebotOffers = (input: AngebotInput): AngebotOffer[] =>
  selectableChildKeys(input.tiergruppe, input.transientState)
    .map(key =>
      key === App.THH_HUND || key === App.THH_PFERD
        ? toThhOffer(input, key)
        : toTkvOffer(input, key),
    )
    .filter((offer): offer is AngebotOffer => offer !== null);

export const effectiveCurrent = (
  offers: readonly AngebotOffer[],
  angebot: Pick<TransientState['angebot'], 'current'>,
): string | null =>
  offers.some(offer => offer.key === angebot.current) ? angebot.current : (offers[0]?.key ?? null);

const UNSETTLED_STATUSES: ReadonlySet<TgvPricingEntry['status']> = new Set([
  'queued',
  'running',
  'failed',
]);

export const isOfferPriced = (
  offer: Pick<AngebotOffer, 'key' | 'state'>,
  pricing: Record<string, TgvPricingEntry>,
): boolean => {
  const status = pricing[offer.key]?.status;
  return hasPricedSelectedLinie(offer.state) && !(status && UNSETTLED_STATUSES.has(status));
};
